/* eslint-disable unicorn/prevent-abbreviations, unicorn/no-null, @typescript-eslint/no-unnecessary-condition */

import type { FC } from 'react';
import { useMemo, useState } from 'react';
import {
    ActionIcon,
    Badge,
    Box,
    Code,
    FileButton,
    Group,
    Loader,
    Menu,
    ScrollArea,
    Text,
    Tooltip,
    UnstyledButton
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
    ChevronLeft,
    ChevronRight,
    Circle,
    CircleCheck,
    CircleDashed,
    Clock,
    Download,
    FileUp,
    GitFork,
    MoreHorizontal,
    Pencil,
    Plus,
    Power,
    Rocket,
    Trash2,
    Upload,
    Zap
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AccountRole, ProjectVersionStatus } from '@newo/api/gateway';
import type { TProjectVersion } from '@newo/api/gateway';
import useDesignerFilesStore from '../DesignerFiles.store';
import ActivateFromRegistryForm from '../ActivateFromRegistryForm';
import ForkVersionForm from '../ForkVersionForm';
import ReleaseVersionForm from '../ReleaseVersionForm';
import {
    useActivateLocalVersionMutation,
    useCustomerLinkQuery
} from '../CustomerLinks.queries';
import {
    useCreateVersionMutation,
    useDeleteVersionMutation,
    useExportVersionMutation,
    useImportVersionMutation,
    useProjectVersionsQuery,
    usePublishVersionMutation,
    useUpdateVersionMutation
} from '../ProjectVersions.queries';
import type { TOptimisticProjectVersion } from '../ProjectVersions.queries';
import { getDirtyFilePaths } from '../FilesTab.utils';
import { useSaveDirtyFilesWithConflictResolution } from '../useSaveDirtyFilesWithConflictResolution';
import RenamePrefixForm from './RenamePrefixForm';
import { formatRelative, formatVersionLabel } from './VersionsSidebar.utils';
import { useAccountQuery } from '@/services/customer/account/account.queries';
import { gcsClient } from '@/utils/api/gcsClient.service';
import { cn } from '@/utils';

interface VersionsSidebarProps {
    projectIdn?: string;
    selectedVersionId: string | null;
    isCollapsed: boolean;
    onToggle: () => void;
    onVersionSelect: (v: TProjectVersion) => void;
}

interface StatusVisual {
    Icon: LucideIcon;
    colorClass: string;
    label: string;
    spin?: boolean;
}

const STATUS_VISUAL: Record<ProjectVersionStatus, StatusVisual> = {
    [ProjectVersionStatus.Pending]: {
        Icon: Circle,
        colorClass: 'text-gray-6',
        label: 'In development — not ready for use'
    },
    [ProjectVersionStatus.Validating]: {
        Icon: CircleDashed,
        colorClass: 'text-yellow-7',
        label: 'Validating…',
        spin: true
    },
    [ProjectVersionStatus.Published]: {
        Icon: CircleCheck,
        colorClass: 'text-green-6',
        label: 'Published — immutable, ready for use'
    }
};

const VersionsSidebar: FC<VersionsSidebarProps> = ({
    projectIdn,
    selectedVersionId,
    isCollapsed,
    onToggle,
    onVersionSelect
}) => {
    const versions = useProjectVersionsQuery(projectIdn);
    const account = useAccountQuery();
    const customerLink = useCustomerLinkQuery(projectIdn);
    const createVersionMutation = useCreateVersionMutation();
    const publishVersionMutation = usePublishVersionMutation();
    const saveDirtyFilesWithConflictResolution =
        useSaveDirtyFilesWithConflictResolution();
    const deleteVersionMutation = useDeleteVersionMutation();
    const updateVersionMutation = useUpdateVersionMutation();
    const exportVersionMutation = useExportVersionMutation();
    const importVersionMutation = useImportVersionMutation();
    const activateLocalMutation = useActivateLocalVersionMutation();
    const [openMenuVersionId, setOpenMenuVersionId] = useState<string | null>(
        null
    );
    const [anchorPos, setAnchorPos] = useState<{ x: number; y: number }>({
        x: 0,
        y: 0
    });
    const [
        isActionsMenuOpened,
        { open: handleActionsMenuOpen, close: handleActionsMenuClose }
    ] = useDisclosure(false);

    const isAdmin = account.data?.role === AccountRole.Admin;
    const isImportDisabled = !projectIdn || importVersionMutation.isPending;

    const activeLocalVersionId = customerLink.data?.project_version_id ?? null;

    const versionLabelById = useMemo(() => {
        const map = new Map<string, string>();
        for (const v of versions.data ?? []) {
            map.set(v.id, formatVersionLabel(v));
        }
        return map;
    }, [versions.data]);

    const activeMenuVersion = useMemo(
        () =>
            (versions.data ?? []).find((v) => v.id === openMenuVersionId) ??
            null,
        [versions.data, openMenuVersionId]
    );

    const handleRowContextMenu = (
        version: TOptimisticProjectVersion,
        event: React.MouseEvent
    ) => {
        event.preventDefault();
        if (version.isOptimistic) {
            return;
        }
        setAnchorPos({ x: event.clientX, y: event.clientY });
        setOpenMenuVersionId(version.id);
    };

    const handleIconClick = (
        version: TProjectVersion,
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setAnchorPos({ x: rect.right, y: rect.bottom });
        setOpenMenuVersionId((prev) =>
            prev === version.id ? null : version.id
        );
    };

    const closeRowMenu = () => {
        setOpenMenuVersionId(null);
    };

    const handleNewVersion = () => {
        handleActionsMenuClose();
        if (!projectIdn) {
            return;
        }
        createVersionMutation.mutate({ projectIdn });
    };

    const handlePublish = async (version: TProjectVersion) => {
        if (!projectIdn) {
            return;
        }
        const dirtyFilePaths = getDirtyFilePaths(
            useDesignerFilesStore.getState().openFiles
        );
        if (dirtyFilePaths.length > 0) {
            const isSaved =
                await saveDirtyFilesWithConflictResolution(dirtyFilePaths);
            if (!isSaved) {
                return;
            }
        }
        publishVersionMutation.mutate({ versionId: version.id, projectIdn });
    };

    const handleForkFromRegistry = () => {
        handleActionsMenuClose();
        const id = modals.open({
            title: 'Fork from Registry',
            centered: true,
            children: (
                <ForkVersionForm
                    projectIdn={projectIdn}
                    onClose={() => {
                        modals.close(id);
                    }}
                />
            )
        });
    };

    const handleForkFromVersion = (version: TProjectVersion) => {
        if (!projectIdn) {
            return;
        }
        createVersionMutation.mutate({
            projectIdn,
            sourceProjectVersionId: version.id
        });
    };

    const handleDeploy = (version: TProjectVersion) => {
        const id = modals.open({
            title: 'Deploy version',
            centered: true,
            children: (
                <ReleaseVersionForm
                    projectVersionId={version.id}
                    onClose={() => {
                        modals.close(id);
                    }}
                />
            )
        });
    };

    const handleDelete = (version: TProjectVersion) => {
        if (!projectIdn) {
            return;
        }
        const isPublishedVersion =
            version.status === ProjectVersionStatus.Published;
        modals.openConfirmModal({
            title: 'Delete version',
            centered: true,
            children: (
                <Text size="sm">
                    Are you sure you want to delete{' '}
                    <Code>{formatVersionLabel(version)}</Code>?
                    {isPublishedVersion
                        ? ' This version is published and may be deployed — deleting it can break running agents.'
                        : ''}{' '}
                    This action cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: () => {
                deleteVersionMutation.mutate({
                    versionId: version.id,
                    projectIdn
                });
                gcsClient.cancelMint(version.id);
            }
        });
    };

    const handleExport = (version: TProjectVersion) => {
        exportVersionMutation.mutate(version);
    };

    const handleActivate = (version: TProjectVersion) => {
        if (!projectIdn) {
            return;
        }
        activateLocalMutation.mutate({
            projectIdn,
            projectVersionId: version.id
        });
    };

    const handleActivateFromRegistry = () => {
        handleActionsMenuClose();
        const id = modals.open({
            title: 'Activate Module from Registry',
            centered: true,
            children: (
                <ActivateFromRegistryForm
                    projectIdn={projectIdn}
                    onClose={() => {
                        modals.close(id);
                    }}
                />
            )
        });
    };

    const handleImportFile = (file: File | null) => {
        handleActionsMenuClose();
        if (!file || !projectIdn) {
            return;
        }
        importVersionMutation.mutate({
            archive: file,
            params: { project_idn: projectIdn }
        });
    };

    const handleRename = (version: TProjectVersion) => {
        if (!projectIdn) {
            return;
        }
        const id = modals.open({
            title: 'Rename version',
            centered: true,
            children: (
                <RenamePrefixForm
                    initialPrefix={version.version_prefix}
                    isPending={updateVersionMutation.isPending}
                    onCancel={() => {
                        modals.close(id);
                    }}
                    onSubmit={(next) => {
                        updateVersionMutation.mutate(
                            {
                                versionId: version.id,
                                projectIdn,
                                data: { version_prefix: next }
                            },
                            {
                                onSuccess: () => {
                                    modals.close(id);
                                }
                            }
                        );
                    }}
                />
            )
        });
    };

    const rowMenu = (
        <Menu
            withinPortal
            opened={openMenuVersionId !== null}
            position="bottom-start"
            shadow="md"
            onChange={(open) => {
                if (!open) {
                    closeRowMenu();
                }
            }}
        >
            <Menu.Target>
                <Box
                    aria-hidden
                    style={{
                        position: 'fixed',
                        left: anchorPos.x,
                        top: anchorPos.y,
                        width: 0,
                        height: 0,
                        pointerEvents: 'none'
                    }}
                />
            </Menu.Target>
            <Menu.Dropdown>
                {activeMenuVersion
                    ? (() => {
                          const v = activeMenuVersion;
                          const isPublished =
                              v.status === ProjectVersionStatus.Published;
                          return (
                              <>
                                  {!isPublished && (
                                      <Menu.Item
                                          disabled={
                                              !projectIdn ||
                                              (publishVersionMutation.isPending &&
                                                  publishVersionMutation
                                                      .variables?.versionId ===
                                                      v.id)
                                          }
                                          leftSection={<Upload size={14} />}
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              closeRowMenu();
                                              void handlePublish(v);
                                          }}
                                      >
                                          Publish
                                      </Menu.Item>
                                  )}
                                  <Menu.Item
                                      disabled={!projectIdn}
                                      leftSection={<Pencil size={14} />}
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          closeRowMenu();
                                          handleRename(v);
                                      }}
                                  >
                                      Rename
                                  </Menu.Item>
                                  <Menu.Item
                                      disabled={
                                          exportVersionMutation.isPending
                                              ? exportVersionMutation.variables
                                                    ?.id === v.id
                                              : false
                                      }
                                      leftSection={<Download size={14} />}
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          closeRowMenu();
                                          handleExport(v);
                                      }}
                                  >
                                      Export…
                                  </Menu.Item>
                                  {isPublished &&
                                  activeLocalVersionId !== v.id ? (
                                      <Menu.Item
                                          disabled={
                                              !projectIdn ||
                                              activateLocalMutation.isPending
                                          }
                                          leftSection={<Power size={14} />}
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              closeRowMenu();
                                              handleActivate(v);
                                          }}
                                      >
                                          Activate
                                      </Menu.Item>
                                  ) : null}
                                  {isPublished ? (
                                      <Menu.Item
                                          disabled={
                                              !projectIdn ||
                                              createVersionMutation.isPending
                                          }
                                          leftSection={<GitFork size={14} />}
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              closeRowMenu();
                                              handleForkFromVersion(v);
                                          }}
                                      >
                                          Fork from this version
                                      </Menu.Item>
                                  ) : null}
                                  {isPublished && isAdmin ? (
                                      <Menu.Item
                                          leftSection={<Rocket size={14} />}
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              closeRowMenu();
                                              handleDeploy(v);
                                          }}
                                      >
                                          Deploy
                                      </Menu.Item>
                                  ) : null}
                                  <Menu.Item
                                      color="red"
                                      leftSection={<Trash2 size={14} />}
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          closeRowMenu();
                                          handleDelete(v);
                                      }}
                                  >
                                      Delete
                                  </Menu.Item>
                              </>
                          );
                      })()
                    : null}
            </Menu.Dropdown>
        </Menu>
    );

    if (isCollapsed) {
        return (
            <Box className="flex h-full flex-col bg-gray-1">
                <Box className="flex h-10 shrink-0 items-center justify-center border-b border-gray-3">
                    <Tooltip label="Expand sidebar" position="right">
                        <ActionIcon
                            aria-label="Expand sidebar"
                            size="sm"
                            variant="subtle"
                            onClick={onToggle}
                        >
                            <ChevronRight size={14} />
                        </ActionIcon>
                    </Tooltip>
                </Box>
                <ScrollArea className="flex-1">
                    <Box className="flex flex-col">
                        {(versions.data ?? []).map((version) => {
                            const isActive = version.id === selectedVersionId;
                            const isOptimisticVersion = version.isOptimistic;
                            const label = isOptimisticVersion
                                ? 'Creating…'
                                : formatVersionLabel(version);
                            const activeLabelSuffix =
                                activeLocalVersionId === version.id
                                    ? ' · active'
                                    : '';
                            const tooltipLabel = isOptimisticVersion
                                ? label
                                : `${label} · ${version.status}${activeLabelSuffix}`;
                            return (
                                <Tooltip
                                    withinPortal
                                    key={version.id}
                                    label={tooltipLabel}
                                    position="right"
                                >
                                    <UnstyledButton
                                        aria-disabled={isOptimisticVersion}
                                        className={cn(
                                            'flex flex-col items-center gap-1 border-b border-gray-3 px-1 py-2',
                                            isOptimisticVersion
                                                ? 'cursor-default opacity-60'
                                                : 'cursor-pointer hover:bg-gray-2',
                                            isActive &&
                                                !isOptimisticVersion &&
                                                'border-l-2 border-l-blue-5 bg-blue-1'
                                        )}
                                        disabled={isOptimisticVersion}
                                        onClick={() => {
                                            if (isOptimisticVersion) {
                                                return;
                                            }
                                            onVersionSelect(version);
                                        }}
                                        onContextMenu={(e) => {
                                            handleRowContextMenu(version, e);
                                        }}
                                        onKeyDown={(e) => {
                                            if (isOptimisticVersion) {
                                                return;
                                            }
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                onVersionSelect(version);
                                            }
                                        }}
                                    >
                                        {isOptimisticVersion ? (
                                            <Loader size={10} />
                                        ) : (
                                            (() => {
                                                const visual =
                                                    STATUS_VISUAL[
                                                        version.status
                                                    ];
                                                const StatusIcon = visual.Icon;
                                                return (
                                                    <StatusIcon
                                                        aria-label={
                                                            visual.label
                                                        }
                                                        className={cn(
                                                            'shrink-0',
                                                            visual.colorClass,
                                                            visual.spin &&
                                                                'animate-spin'
                                                        )}
                                                        size={12}
                                                    />
                                                );
                                            })()
                                        )}
                                        <Text className="font-mono" size="xs">
                                            {isOptimisticVersion
                                                ? '…'
                                                : version.version_increment}
                                        </Text>
                                        {!isOptimisticVersion &&
                                            activeLocalVersionId ===
                                                version.id && (
                                                <Badge
                                                    aria-label="Active runtime version"
                                                    color="dark"
                                                    leftSection={
                                                        <Zap size={8} />
                                                    }
                                                    radius="sm"
                                                    size="xs"
                                                    variant="filled"
                                                >
                                                    Active
                                                </Badge>
                                            )}
                                    </UnstyledButton>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </ScrollArea>
                {rowMenu}
            </Box>
        );
    }

    return (
        <Box className="flex h-full flex-col bg-gray-1">
            <Box className="flex h-10 shrink-0 items-center justify-between border-b border-gray-3 px-3">
                <Text fw={700} size="sm">
                    Versions
                </Text>
                <Group gap={4} wrap="nowrap">
                    <Menu
                        withinPortal
                        closeOnItemClick={false}
                        opened={isActionsMenuOpened}
                        position="bottom-end"
                        shadow="md"
                        onClose={handleActionsMenuClose}
                        onOpen={handleActionsMenuOpen}
                    >
                        <Menu.Target>
                            <ActionIcon
                                aria-label="Version actions"
                                size="sm"
                                variant="subtle"
                            >
                                <MoreHorizontal size={14} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item
                                disabled={
                                    !projectIdn ||
                                    createVersionMutation.isPending
                                }
                                leftSection={<Plus size={14} />}
                                onClick={handleNewVersion}
                            >
                                New Blank Version
                            </Menu.Item>
                            <Menu.Item
                                leftSection={<GitFork size={14} />}
                                onClick={handleForkFromRegistry}
                            >
                                Fork from Registry
                            </Menu.Item>
                            <Menu.Divider />
                            <FileButton
                                accept=".zip,application/zip,application/x-zip-compressed"
                                disabled={isImportDisabled}
                                onChange={handleImportFile}
                            >
                                {({ onClick }) => (
                                    <Menu.Item
                                        disabled={isImportDisabled}
                                        leftSection={<FileUp size={14} />}
                                        onClick={onClick}
                                    >
                                        Import from Archive…
                                    </Menu.Item>
                                )}
                            </FileButton>
                            <Menu.Item
                                disabled={!projectIdn}
                                leftSection={<Power size={14} />}
                                onClick={handleActivateFromRegistry}
                            >
                                Activate Module from Registry…
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                    <Tooltip label="Collapse sidebar" position="right">
                        <ActionIcon
                            aria-label="Collapse sidebar"
                            size="sm"
                            variant="subtle"
                            onClick={onToggle}
                        >
                            <ChevronLeft size={14} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            </Box>

            <ScrollArea className="flex-1">
                {versions.data?.length ? (
                    <Box className="flex flex-col">
                        {versions.data.map((version) => {
                            const isActive = version.id === selectedVersionId;
                            const isOptimisticVersion = version.isOptimistic;
                            const isPublished =
                                version.status ===
                                ProjectVersionStatus.Published;
                            const label = isOptimisticVersion
                                ? 'Creating new version…'
                                : formatVersionLabel(version);
                            const forkedFromId =
                                version.forked_project_version_id;
                            const forkedFromLabel = forkedFromId
                                ? (versionLabelById.get(forkedFromId) ?? null)
                                : null;

                            return (
                                <UnstyledButton
                                    aria-disabled={isOptimisticVersion}
                                    className={cn(
                                        'group flex min-w-[12rem] flex-col gap-1 border-b border-gray-3 px-3 py-2',
                                        isOptimisticVersion
                                            ? 'cursor-default opacity-60'
                                            : 'cursor-pointer hover:bg-gray-2',
                                        isActive &&
                                            !isOptimisticVersion &&
                                            'border-l-2 border-l-blue-5 bg-blue-1'
                                    )}
                                    disabled={isOptimisticVersion}
                                    key={version.id}
                                    onClick={() => {
                                        if (isOptimisticVersion) {
                                            return;
                                        }
                                        onVersionSelect(version);
                                    }}
                                    onContextMenu={(e) => {
                                        handleRowContextMenu(version, e);
                                    }}
                                    onKeyDown={(e) => {
                                        if (isOptimisticVersion) {
                                            return;
                                        }
                                        if (
                                            e.key === 'Enter' ||
                                            e.key === ' '
                                        ) {
                                            onVersionSelect(version);
                                        }
                                    }}
                                >
                                    <Group gap="sm" wrap="nowrap">
                                        {isOptimisticVersion ? (
                                            <Loader size={10} />
                                        ) : (
                                            (() => {
                                                const visual =
                                                    STATUS_VISUAL[
                                                        version.status
                                                    ];
                                                const StatusIcon = visual.Icon;
                                                return (
                                                    <Tooltip
                                                        withinPortal
                                                        label={visual.label}
                                                        position="top"
                                                    >
                                                        <StatusIcon
                                                            aria-label={
                                                                visual.label
                                                            }
                                                            className={cn(
                                                                'shrink-0',
                                                                visual.colorClass,
                                                                visual.spin &&
                                                                    'animate-spin'
                                                            )}
                                                            size={14}
                                                        />
                                                    </Tooltip>
                                                );
                                            })()
                                        )}
                                        <Text
                                            truncate
                                            className="min-w-0 flex-1 font-mono"
                                            fw={600}
                                            size="sm"
                                        >
                                            {label}
                                        </Text>
                                        {!isOptimisticVersion &&
                                            activeLocalVersionId ===
                                                version.id && (
                                                <Tooltip
                                                    withinPortal
                                                    label="Active — currently handling runtime events"
                                                    position="top"
                                                >
                                                    <Badge
                                                        color="dark"
                                                        leftSection={
                                                            <Zap size={10} />
                                                        }
                                                        radius="sm"
                                                        size="xs"
                                                        variant="filled"
                                                    >
                                                        Active
                                                    </Badge>
                                                </Tooltip>
                                            )}
                                        {!isOptimisticVersion && (
                                            <Group
                                                className="shrink-0"
                                                gap={4}
                                                wrap="nowrap"
                                            >
                                                {!isPublished &&
                                                    (() => {
                                                        const isPublishing =
                                                            publishVersionMutation.isPending &&
                                                            publishVersionMutation
                                                                .variables
                                                                ?.versionId ===
                                                                version.id;
                                                        return (
                                                            <Box
                                                                className={cn(
                                                                    'inline-flex transition-opacity focus-within:opacity-100 group-hover:opacity-100',
                                                                    isPublishing
                                                                        ? 'opacity-100'
                                                                        : 'opacity-0'
                                                                )}
                                                            >
                                                                <Tooltip
                                                                    label="Publish"
                                                                    position="top"
                                                                >
                                                                    <ActionIcon
                                                                        aria-label="Publish version"
                                                                        loading={
                                                                            isPublishing
                                                                        }
                                                                        size="xs"
                                                                        variant="subtle"
                                                                        onClick={(
                                                                            e
                                                                        ) => {
                                                                            e.stopPropagation();
                                                                            void handlePublish(
                                                                                version
                                                                            );
                                                                        }}
                                                                    >
                                                                        <Upload
                                                                            size={
                                                                                12
                                                                            }
                                                                        />
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            </Box>
                                                        );
                                                    })()}

                                                <ActionIcon
                                                    aria-label="Version row actions"
                                                    size="xs"
                                                    variant="subtle"
                                                    onClick={(e) => {
                                                        handleIconClick(
                                                            version,
                                                            e
                                                        );
                                                    }}
                                                >
                                                    <MoreHorizontal size={12} />
                                                </ActionIcon>
                                            </Group>
                                        )}
                                    </Group>

                                    {!isOptimisticVersion && forkedFromId ? (
                                        <Group gap={6} wrap="nowrap">
                                            <GitFork
                                                className="shrink-0"
                                                size={12}
                                            />
                                            <Text
                                                truncate
                                                c="gray.6"
                                                className="min-w-0"
                                                size="xs"
                                            >
                                                {forkedFromLabel
                                                    ? `forked from ${forkedFromLabel}`
                                                    : 'forked from external registry'}
                                            </Text>
                                        </Group>
                                    ) : null}

                                    {isOptimisticVersion ? null : (
                                        <Group gap={6} wrap="nowrap">
                                            <Clock
                                                className="shrink-0"
                                                size={12}
                                            />
                                            <Text
                                                truncate
                                                c="gray.6"
                                                className="min-w-0"
                                                size="xs"
                                            >
                                                Updated{' '}
                                                {formatRelative(
                                                    version.updated_at
                                                )}
                                            </Text>
                                        </Group>
                                    )}
                                </UnstyledButton>
                            );
                        })}
                    </Box>
                ) : (
                    <Box className="flex flex-col items-center gap-2 px-3 py-8 text-center">
                        <Text fw={600} size="sm">
                            No versions yet
                        </Text>
                        <Text c="gray.5" size="xs">
                            Create a version to get started.
                        </Text>
                    </Box>
                )}
            </ScrollArea>
            {rowMenu}
        </Box>
    );
};

export default VersionsSidebar;
